import { useMemo } from 'react';
import { gql } from 'graphql-request';
import { PracticeAnalyticsAggregations, PracticeAnalyticsApi, PracticeAnalyticsTypes } from '@frontend/api-analytics';
import { useTranslation } from '@frontend/i18n';
import { useScopedQuery } from '@frontend/scope';
import { useAlert } from '@frontend/design-system';
import { queryKeys } from '../../../query-keys';
import { getPracticeAnalyticsQueryVariables } from '../helpers/query-variables';
import { usePracticeAnalyticsShallowStore } from './use-practice-analytics-store';

const patients = `
  details(start: $start, end: $end, step: $step) {
    patients {
      id
      productionAmount
      cancelledAppointmentDate
      cancelledAppointmentType
      rescheduledAppointmentDate
      nextAppointmentDate
      FirstName: firstName
      LastName: lastName
      PMID: pmid
      MobilePhone: mobilePhone
      HomePhone: homePhone
      WorkPhone: workPhone
      Email: email
      PreferredName: preferredName
      Birthdate: birthdate
      Gender: gender
    }
  }
`;

const query = (fetchPatients?: boolean) => gql`
  query ($start: Int!, $end: Int!, $step: TimeStep!) {
    location {
      missedAppointments {
        ${fetchPatients ? patients : ''}
        totals(start: $start, end: $end, step: $step) {
          cancelled
          cancelledUnscheduled
          recapturedProduction
          rescheduled
          totalPersonsCount
          totalProduction
          totalProductionRisk
          unscheduledOpportunity
        }
      }
    }
  }
`;

export const useGetCancellationsMetric = (fetchPatients?: boolean) => {
  const alert = useAlert();
  const { t } = useTranslation('analytics');

  const { demoData, filters, isDemoAccount } = usePracticeAnalyticsShallowStore('demoData', 'filters', 'isDemoAccount');

  const { data, isLoading } = useScopedQuery({
    queryKey: queryKeys.practiceAnalyticsCharts(
      `cancellationsSummary-${isDemoAccount}-${JSON.stringify(filters)}-${fetchPatients}`
    ),
    queryFn: () =>
      isDemoAccount || !filters.locations?.length
        ? null
        : PracticeAnalyticsApi.getPracticeAnalyticsRecords<PracticeAnalyticsTypes.CancellationsResponse>({
            locationIds: filters.locations,
            queries: [query(fetchPatients)],
            variables: getPracticeAnalyticsQueryVariables(filters),
          }),
    onError: () => {
      alert.error(t("Couldn't load the cancellations data. Please try again."));
    },
    select: (data) => (isDemoAccount ? demoData?.cancellations : data),
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { aggregatedData, rawData } = useMemo(() => {
    return PracticeAnalyticsAggregations.cancellations(data);
  }, [data]);

  return {
    aggregatedCancellationsData: aggregatedData,
    isLoading,
    rawCancellationsData: rawData,
  };
};
